import { useEffect, useState } from "react";
import { McpTokenCard, McpTokenEdit, McpTokenTable, NewMcpTokenForm } from "../components/McpTokens";
import { Button } from "../components/Button";
import { Alert, Loading } from "../components/Card";
import { authErrText } from "../lib/auth";
import { mcpTokenApi } from "../lib/mcpTokens";
import type { McpToken } from "../lib/mcpTokens";
import { projectApi } from "../lib/project";
import type { Project } from "../lib/project";
import { tx } from "../i18n";

/* ══ 설정 › MCP 토큰(관리자만) — 시안 docs/screens/pr-port.md §23 · 결정 docs/decisions/agent-token.md ══
   러너 화면과 같은 모양이다. 열 때 한 번 읽고, 쓴 뒤에 다시 읽는다 — 타이머로 박동하지 않는다.
   원문 토큰은 발급 응답에만 있어서, 카드를 닫으면 다시 볼 길이 없다. */

const mcpUrl = () => `${location.origin}/mcp`;

export function McpTokensSection() {
  const [rows, setRows] = useState<McpToken[] | null>(null);
  const [projects, setProjects] = useState<Pick<Project, "id" | "name">[]>([]);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [formErr, setFormErr] = useState<string | null>(null);
  const [issued, setIssued] = useState<{ name: string; token: string } | null>(null);
  const [picked, setPicked] = useState<string | null>(null);

  function load() {
    mcpTokenApi
      .list()
      .then((r) => {
        setRows(r);
        setLoadErr(null);
      })
      .catch((e) => setLoadErr(authErrText(e)));
  }
  useEffect(() => {
    load();
    // 프로젝트 이름은 범위 칸의 이름표일 뿐이다 — 못 읽으면 id 그대로 보인다(목록은 막지 않는다).
    projectApi
      .list()
      .then((p) => setProjects(p.map(({ id, name }) => ({ id, name }))))
      .catch(() => setProjects([]));
  }, []);

  async function run(f: () => Promise<void>) {
    setBusy(true);
    setFormErr(null);
    try {
      await f();
      load();
    } catch (e) {
      setFormErr(authErrText(e));
    } finally {
      setBusy(false);
    }
  }

  const pickedRow = rows?.find((t) => t.name === picked) ?? null;
  const expired = rows?.filter((t) => t.expired).length ?? 0;

  return (
    <>
      <h2>{tx("mcpTokens.title")}</h2>
      <p className="muted">{tx("mcpTokens.lead")}</p>
      <div className="rbar">
        <span className="rbar__sum">{rows ? tx("mcpTokens.summary", { n: rows.length, expired }) : null}</span>
        {!adding ? (
          <Button
            variant="primary"
            onClick={() => {
              setAdding(true);
              setPicked(null);
              setIssued(null);
              setFormErr(null);
            }}
          >
            {tx("mcpTokens.add")}
          </Button>
        ) : null}
      </div>
      {issued ? <McpTokenCard name={issued.name} token={issued.token} mcpUrl={mcpUrl()} onClose={() => setIssued(null)} /> : null}
      {adding ? (
        <NewMcpTokenForm
          projects={projects}
          busy={busy}
          error={formErr}
          onCancel={() => setAdding(false)}
          onSubmit={(b) =>
            void run(async () => {
              const r = await mcpTokenApi.create(b);
              setAdding(false);
              setIssued({ name: r.name, token: r.token });
            })
          }
        />
      ) : null}
      {pickedRow ? (
        <McpTokenEdit
          t={pickedRow}
          projects={projects}
          busy={busy}
          error={formErr}
          onClose={() => setPicked(null)}
          onSave={(b) =>
            void run(async () => {
              if (Object.keys(b).length === 0) return setPicked(null);
              const r = await mcpTokenApi.update(pickedRow.name, b);
              setPicked(r.name);
            })
          }
          onRevoke={() =>
            void run(async () => {
              await mcpTokenApi.revoke(pickedRow.name);
              setPicked(null);
            })
          }
        />
      ) : null}
      {loadErr ? <Alert tone="error">{tx("mcpTokens.loadFailed", { err: loadErr })}</Alert> : null}
      {rows ? (
        <McpTokenTable
          rows={rows}
          projects={projects}
          picked={picked}
          onPick={(n) => {
            setPicked(picked === n ? null : n);
            setAdding(false);
            setFormErr(null);
          }}
        />
      ) : !loadErr ? (
        <Loading text={tx("mcpTokens.loading")} />
      ) : null}
    </>
  );
}
